import { useState } from "react";
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Mail,
  MessageCircle,
  QrCode,
  Smartphone,
  Zap,
} from "lucide-react";
import Button from "@/components/ui/Button";
import StatusBadge from "@/components/ui/StatusBadge";
import { useToast } from "@/components/ui/Toast";
import { WALLET_RATES } from "@/pages/retailer/wallet/constants";
import { mandateService } from "@/services/apiServices";

interface Props {
  refNo: string;
  status?: string;
  authLink: string;
  checkoutUrl?: string | null;
  qrCode?: string | null;
  rocketpayMandateId?: string | null;
  customerName: string;
  customerMobile: string;
  customerEmail?: string;
  brand: string;
  model: string;
  serialNo: string;
  emi: string;
  tenure: string;
  totalPayable: string;
  firstInstallment: string;
  balanceAfter: number;
  creationCharge?: number;
  qrUrl?: string | null;
  paymentOrderId?: string | null;
  onReset: () => void;
  onViewDetails: () => void;
}

function fmtINR(v: string) {
  const n = Number(v || 0);
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtINRN(n: number) {
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtMobile(v: string) {
  if (!v) return "";
  return `+91 ${v.slice(0, 5)} ${v.slice(5)}`;
}

export default function SuccessScreen({
  refNo,
  status,
  authLink,
  checkoutUrl,
  qrCode,
  rocketpayMandateId,
  customerName,
  customerMobile,
  customerEmail,
  brand,
  model,
  serialNo,
  emi,
  tenure,
  totalPayable,
  firstInstallment,
  balanceAfter,
  creationCharge = WALLET_RATES.CREATION_CHARGE,
  qrUrl,
  paymentOrderId,
  onReset,
  onViewDetails,
}: Props) {
  const toast = useToast();
  const [sendingEmail, setSendingEmail] = useState(false);

  function copy(text: string, message: string) {
    navigator.clipboard
      ?.writeText(text)
      .then(() => toast.success(message))
      .catch(() => toast.error("Could not copy to clipboard"));
  }

  const effectiveCheckoutUrl = checkoutUrl || authLink;
  const isRocketPayCheckout = effectiveCheckoutUrl.startsWith("http");

  function shareWhatsApp() {
    const rawMobile = (customerMobile || "").replace(/\D/g, "");
    const formattedPhone = rawMobile.length === 10 ? `91${rawMobile}` : rawMobile;
    const productInfo = [brand, model].filter(Boolean).join(" ") || "E-Mandate Facility";

    const message = `━━━━━━━━━━━━━━━━━━━━\n📄 *DIGI INDIA SOLUTIONS*\n*E-Mandate Authorization Request*\n━━━━━━━━━━━━━━━━━━━━\n\nDear *${customerName}*,\n\nGreetings from *Digi India Solutions*!\n\nYour digital auto-debit E-Mandate has been created successfully. Please review the details below and complete the authorization:\n\n📌 *Mandate Details:*\n• *Reference No:* \`${refNo}\`\n• *Item / Facility:* ${productInfo}\n• *Monthly EMI:* *₹${emi}*\n• *Tenure:* ${tenure} Months\n• *Total Payable:* ₹${totalPayable}\n• *First Installment:* ${firstInstallment || "As Scheduled"}\n\n👉 *Authorise Your Mandate Here:*\n${effectiveCheckoutUrl}\n\nℹ️ *Important Information:*\n• 100% paperless & secure bank authorization via UPI or Net Banking.\n• Please complete authorization within 24 hours to activate your facility.\n\n━━━━━━━━━━━━━━━━━━━━\n🏢 *Digi India Solutions*\n🌐 https://eman.digiindiasolutions.com\n📞 Support: support@digiindiasolutions.com\n━━━━━━━━━━━━━━━━━━━━`;

    const url = formattedPhone
      ? `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encodeURIComponent(message)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function handleSendEmail() {
    if (!customerEmail && !customerName) {
      toast.error("No customer email available");
      return;
    }
    setSendingEmail(true);
    try {
      const res = await mandateService.sendAuthEmail(refNo);
      toast.success(res?.message || `Authorisation email sent to ${customerEmail || "customer"}`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to dispatch email via SMTP");
    } finally {
      setSendingEmail(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-[640px]">
      <div className="flex flex-col items-center text-center">
        <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-success-bg">
          <CheckCircle2 className="h-10 w-10 text-success" />
        </div>
        <h2 className="mt-4 text-2xl font-semibold text-ink">
          Mandate created successfully
        </h2>
        <p className="mt-1.5 text-sm text-ink-secondary">
          {rocketpayMandateId
            ? "Synced live with Gateway. Authorisation link generated and emailed to customer."
            : "The authorisation link has been generated and sent to the customer."}
        </p>
      </div>

      <div className="mt-6 rounded-[10px] border border-line bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-ink-secondary">Reference Number</span>
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold text-ink">{refNo}</span>
            <button
              type="button"
              onClick={() => copy(refNo, "Reference number copied")}
              className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
              aria-label="Copy reference number"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>

        {rocketpayMandateId && (
          <>
            <div className="my-3 h-px bg-slate-100" />
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] text-ink-secondary flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5 text-primary-600" /> Gateway Mandate ID
              </span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs font-semibold text-primary-700 bg-primary-50 px-2 py-0.5 rounded border border-primary-100">
                  {rocketpayMandateId}
                </span>
                <button
                  type="button"
                  onClick={() => copy(rocketpayMandateId, "Gateway Mandate ID copied")}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                  aria-label="Copy Gateway ID"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </>
        )}

        <div className="my-3 h-px bg-slate-100" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-ink-secondary">Customer</span>
          <span className="text-right text-sm font-medium text-ink">
            {customerName || "—"} · {fmtMobile(customerMobile) || "—"}
          </span>
        </div>
        <div className="my-3 h-px bg-slate-100" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-ink-secondary">Product</span>
          <span className="text-right text-sm font-medium text-ink">
            {[brand, model].filter(Boolean).join(" ") || "—"}
            {serialNo ? <span className="block text-xs font-normal text-ink-secondary">{serialNo}</span> : null}
          </span>
        </div>
        <div className="my-3 h-px bg-slate-100" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-ink-secondary">EMI</span>
          <span className="text-right text-sm font-medium text-ink">
            {fmtINR(emi)} × {tenure || "—"} months
          </span>
        </div>
        <div className="my-3 h-px bg-slate-100" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-ink-secondary">Total Payable</span>
          <span className="text-right text-sm font-medium text-ink">{fmtINR(totalPayable)}</span>
        </div>
        <div className="my-3 h-px bg-slate-100" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-ink-secondary">First Installment</span>
          <span className="text-right text-sm font-medium text-ink">{firstInstallment || "—"}</span>
        </div>
        <div className="my-3 h-px bg-slate-100" />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-ink-secondary">Status</span>
          <StatusBadge status={status || "CREATED"} />
        </div>
        {/* {rocketpayMandateId && (
          <>
            <div className="my-3 h-px bg-slate-100" />
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] text-ink-secondary">Gateway ID</span>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs font-semibold text-primary">{rocketpayMandateId}</span>
                <button
                  type="button"
                  onClick={() => copy(rocketpayMandateId, "Gateway Mandate ID copied")}
                  className="text-ink-muted hover:text-ink cursor-pointer"
                  title="Copy Gateway ID"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </>
        )} */}
      </div>

      {/* UPI AutoPay Mandate QR / Intent (Commented Out)
      {qrCode && (
        <div className="mt-4 rounded-[10px] border border-line bg-page p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-ink uppercase tracking-wider">UPI AutoPay Mandate QR / Intent</span>
            <button
              type="button"
              onClick={() => copy(qrCode, "UPI Intent copied")}
              className="text-xs text-primary font-medium hover:underline cursor-pointer flex items-center gap-1"
            >
              <Copy className="h-3 w-3" /> Copy Intent
            </button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              readOnly
              value={qrCode}
              className="h-[36px] w-full rounded border border-line bg-white px-3 font-mono text-[11px] text-ink-secondary focus:outline-none"
            />
          </div>
        </div>
      )}
      */}

      {/* Gateway Checkout URL (Commented Out)
      <div className="mt-5">
        <div className="flex items-center justify-between">
          <p className="text-[13px] font-medium text-ink-secondary">
            {checkoutUrl ? "Gateway Checkout URL" : "Authorisation Link"}
          </p>
          {checkoutUrl && (
            <span className="rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 border border-emerald-200">
              Live Gateway Checkout
            </span>
          )}
        </div>
        <div className="mt-1.5 flex overflow-hidden rounded-lg border border-slate-300 bg-white">
          <input
            readOnly
            value={effectiveCheckoutUrl}
            className="h-[42px] w-full bg-page px-3.5 text-[13px] font-mono text-ink-secondary focus:outline-none truncate"
          />
          <button
            type="button"
            onClick={() => copy(effectiveCheckoutUrl, "Checkout URL copied to clipboard")}
            className="inline-flex shrink-0 items-center gap-1.5 border-l border-slate-300 bg-white px-4 text-sm font-medium text-ink hover:bg-slate-50 cursor-pointer"
          >
            <Copy className="h-4 w-4" />
            Copy
          </button>
        </div>
      </div>
      */}

      <div className="mt-4 flex items-center justify-between rounded-lg border border-line bg-page px-4 py-3">
        <span className="text-xs text-ink-secondary">
          {fmtINRN(creationCharge)} deducted · Available balance {fmtINRN(balanceAfter)}
        </span>
      </div>

      {/* Main Action Buttons */}
      <div className="mt-5 grid grid-cols-1 gap-3">
        {/* {isRocketPayCheckout ? (
          <Button
            variant="primary"
            className="h-11 shadow-sm w-full"
            leftIcon={<ExternalLink className="h-4 w-4" />}
            onClick={() => window.open(effectiveCheckoutUrl, "_blank", "noopener,noreferrer")}
          >
            Open Gateway Checkout
          </Button>
        ) : (
          <Button
            variant="primary"
            className="h-11 w-full"
            leftIcon={<ExternalLink className="h-4 w-4" />}
            onClick={() => window.open(`/auth/${refNo}`, "_blank", "noopener,noreferrer")}
          >
            Preview Auth Portal
          </Button>
        )} */}

        {/* Open UPI QR Simulator (Commented Out)
        {qrUrl ? (
          <Button
            variant="secondary"
            className="h-11 border-primary-200 bg-primary-50/50 text-primary-700 hover:bg-primary-50"
            leftIcon={<QrCode className="h-4 w-4 text-primary-600" />}
            onClick={() => window.open(qrUrl, "_blank", "noopener,noreferrer")}
          >
            Open UPI QR Simulator
          </Button>
        ) : (
          <Button
            variant="secondary"
            className="h-11"
            leftIcon={<ExternalLink className="h-4 w-4" />}
            onClick={() => window.open(`/auth/${refNo}`, "_blank", "noopener,noreferrer")}
          >
            Internal Auth Simulator
          </Button>
        )}
        */}
      </div>

      {/* Share Actions */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Button
          variant="secondary"
          leftIcon={<MessageCircle className="h-4 w-4" />}
          onClick={() => {
            const text = encodeURIComponent(`Hi ${customerName}, please complete your e-mandate authorisation here: ${authLink}`);
            window.open(`https://wa.me/91${customerMobile}?text=${text}`, "_blank");
          }}
        >
          Share WhatsApp
        </Button>
        {/* <Button
          variant="secondary"
          leftIcon={<Smartphone className="h-4 w-4 text-blue-600" />}
          onClick={() => toast.info(`SMS with checkout link sent to +91 ${customerMobile}`)}
        >
          Send SMS
        </Button> */}
        <div>
            {isRocketPayCheckout ? (
          <Button
            variant="primary"
            className="h-11 shadow-sm w-full"
            leftIcon={<ExternalLink className="h-4 w-4" />}
            onClick={() => window.open(effectiveCheckoutUrl, "_blank", "noopener,noreferrer")}
          >
            Open Gateway Checkout
          </Button>
        ) : (
          <Button
            variant="primary"
            className="h-11 w-full"
            leftIcon={<ExternalLink className="h-4 w-4" />}
            onClick={() => window.open(`/auth/${refNo}`, "_blank", "noopener,noreferrer")}
          >
            Preview Auth Portal
          </Button>
        )}
        </div>
        <Button
          variant="secondary"
          leftIcon={<Mail className="h-4 w-4 text-amber-600" />}
          onClick={() => toast.info("Checkout link emailed to the customer")}
        >
          Send Email
        </Button>
        {/* Open Gateway Checkout below WhatsApp (Commented Out)
        <Button
          variant="primary"
          leftIcon={<ExternalLink className="h-4 w-4" />}
          onClick={() => window.open(checkoutUrl || authLink || `/auth/${refNo}`, "_blank", "noopener,noreferrer")}
        >
          {checkoutUrl ? "Open Gateway Checkout" : "Preview Link"}
        </Button>
        */}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 pt-2 border-t border-line">
        <Button variant="secondary" onClick={onReset}>
          Create Another Mandate
        </Button>
        <Button onClick={onViewDetails}>View Mandate Details</Button>
      </div>
    </div>
  );
}